class MotionGenerator {
  constructor(channelRangeList, channel, reversed) {
    this.M_PI = 3.14159265358979323846;
    this.name = '';
    this.enabled = true;
    this.m_min = 1;
    this.m_max = 9999;
    this.updateRate = 100;
    this.period = 2000;
    this.periodRandom = 2000;
    this.periodRandomMode = false;
    this.periodRandomMin = 500;
    this.periodRandomMax = 6000;
    this.amplitude = 60;
    this.amplitudeRandom = 60;
    this.amplitudeRandomMode = false;
    this.amplitudeRandomMin = 20;
    this.amplitudeRandomMax = 60;
    this.offset = 0;
    this.offsetRandom = 0;
    this.offsetRandomMode = false;
    this.offsetRandomMin = 20;
    this.offsetRandomMax = 80;
    this.phase = 0;
    this.phaseRandom = 0;
    this.phaseRandomMode = false;
    this.phaseRandomMin = 0;
    this.phaseRandomMax = 180;
    this.reversed = false;

    this.motionRandomChangeMin = 3000;
    this.motionRandomChangeMax = 10000;

    // Internal state
    this.lastExecutionPeriodChangePeriod = 3000;
    this.randomPeriodExecutionPeriod = 3000;
    this.randomAmplitudeExecutionPeriod = 3000;
    this.randomOffsetExecutionPeriod = 3000;
    this.randomPhaseExecutionPeriod = 3000;
    this.lastExecutionPeriodChange = 0;
    this.lastRandomPeriodExecutionChange = 0;
    this.lastRandomAmplitudeExecutionChange = 0;
    this.lastRandomOffsetExecutionChange = 0;
    this.lastRandomPhaseExecutionChange = 0;
    this.currentPhase = 0;
    this.phaseIncrement = 1;
    this.stopped = false;
    this.stopAt = 0;
    this.lastUpdate = 0;
    this.interval = 0;
    this.nowMillis = Date.now();

    this.initialize(channelRangeList, channel, reversed);
  }

  initialize(channelRangeList, channel, reversed) {
    this.nowMillis = Date.now();

    // Set random periods
    this.lastExecutionPeriodChangePeriod = this.random(this.motionRandomChangeMin, this.motionRandomChangeMax);
    this.randomPeriodExecutionPeriod = this.random(this.motionRandomChangeMin, this.motionRandomChangeMax);
    this.randomAmplitudeExecutionPeriod = this.random(this.motionRandomChangeMin, this.motionRandomChangeMax);
    this.randomOffsetExecutionPeriod = this.random(this.motionRandomChangeMin, this.motionRandomChangeMax);
    this.randomPhaseExecutionPeriod = this.random(this.motionRandomChangeMin, this.motionRandomChangeMax);

    this.lastExecutionPeriodChange = this.nowMillis;
    this.lastRandomPeriodExecutionChange = this.nowMillis;
    this.lastRandomAmplitudeExecutionChange = this.nowMillis;
    this.lastRandomOffsetExecutionChange = this.nowMillis;
    this.lastRandomPhaseExecutionChange = this.nowMillis;
    this.lastUpdate = this.nowMillis;

    this.reversed = reversed;

    // Set min/max ranges
    if (channelRangeList && channelRangeList.length > 0) {
      for (const channelRange of channelRangeList) {
        if (channelRange.name === channel.name) {
          if (channelRange.min > 0) {
            this.m_min = channelRange.min;
          }
          if (channelRange.max > 0) {
            this.m_max = channelRange.max;
          }
          //console.log('Channel ' + channel.name + ' min/max: ' + this.m_min + '/' + this.m_max);
        }
      }
    }

    this.setAmplitude(channel.amp);
    this.setOffset(channel.offset);
    this.setPeriod(channel.period);
    this.setUpdate(channel.update);
    this.setPhase(channel.phase);
    this.setReverse(channel.reverse);
    this.setAmplitudeRandom(channel.ampRan);
    this.setAmplitudeRandomMin(channel.ampMin);
    this.setAmplitudeRandomMax(channel.ampMax);
    this.setPeriodRandom(channel.periodRan);
    this.setPeriodRandomMin(channel.periodMin);
    this.setPeriodRandomMax(channel.periodMax);
    this.setOffsetRandom(channel.offsetRan);
    this.setOffsetRandomMin(channel.offsetMin);
    this.setOffsetRandomMax(channel.offsetMax);
    this.setPhaseRandom(channel.phaseRan);
    this.setPhaseRandomMin(channel.phaseMin);
    this.setPhaseRandomMax(channel.phaseMax);
    this.name = channel.name;
  }

  calculateNext(millis) {
    this.nowMillis = millis;
    let reStr = '';

    if (!this.canUpdate()) {
      return reStr;
    }

    this.checkUpdateRandomExecutionPeriod();

    if (this.periodRandomMode) {
      this.getPeriod(); // Just call to check for random updates
    }

    if (!this.stopped) {
      let pos = this.constrain(
        Math.round(this.getAmplitude() * Math.sin(this.currentPhase + this.getPhase()) + this.getOffset()),
        -90, 90
      );

      if (this.reversed) {
        pos = -pos;
      }

      const ix = this.constrain(
        this.map(pos, -90, 90, 0, 9999),
        this.m_min, this.m_max
      );

      const xx = ix.toString().padStart(4, '0');
      reStr = this.name + xx + 'I' + this.interval;
    }

    this.currentPhase += this.phaseIncrement;
    return reStr;
  }

  canUpdate() {
    if (this.enabled) {
      this.interval = this.nowMillis - this.lastUpdate;
      if (this.interval > this.updateRate) {
        this.lastUpdate = this.nowMillis;
        return true;
      }
    }
    return false;
  }

  checkUpdateRandomExecutionPeriod() {
    if ((this.offsetRandomMode || this.amplitudeRandomMode || this.periodRandomMode) &&
      this.nowMillis > this.lastExecutionPeriodChange + this.lastExecutionPeriodChangePeriod) {

      if (this.offsetRandomMode &&
        this.nowMillis > this.lastRandomOffsetExecutionChange + this.randomOffsetExecutionPeriod) {
        this.randomPeriodExecutionPeriod = this.random(this.motionRandomChangeMin, this.motionRandomChangeMax);
      }

      if (this.amplitudeRandomMode &&
        this.nowMillis > this.lastRandomAmplitudeExecutionChange + this.randomAmplitudeExecutionPeriod) {
        this.randomAmplitudeExecutionPeriod = this.random(this.motionRandomChangeMin, this.motionRandomChangeMax);
      }

      if (this.periodRandomMode &&
        this.nowMillis > this.lastRandomPeriodExecutionChange + this.randomPeriodExecutionPeriod) {
        this.randomOffsetExecutionPeriod = this.random(this.motionRandomChangeMin, this.motionRandomChangeMax);
      }

      this.lastExecutionPeriodChangePeriod = this.random(this.motionRandomChangeMin, this.motionRandomChangeMax);
    }
  }

  getPeriod() {
    if (this.periodRandomMode) {
      if (this.nowMillis > this.lastRandomPeriodExecutionChange + this.randomPeriodExecutionPeriod) {
        this.lastRandomPeriodExecutionChange = this.nowMillis;
        this.updatePeriodRandom();
      }
      return this.periodRandom;
    }
    return this.period;
  }

  updatePeriodRandom() {
    this.periodRandom = this.random(this.periodRandomMin, this.periodRandomMax);
    this.updatePhaseIncrement();
  }

  updatePhaseIncrement() {
    this.phaseIncrement = 2.0 * this.M_PI / (this.getPeriod() / this.updateRate);
  }

  getAmplitude() {
    if (this.amplitudeRandomMode) {
      if (this.nowMillis > this.lastRandomAmplitudeExecutionChange + this.randomAmplitudeExecutionPeriod) {
        this.lastRandomAmplitudeExecutionChange = this.nowMillis;
        this.updateAmplitudeRandom();
      }
      return this.amplitudeRandom;
    }
    return this.amplitude;
  }

  updateAmplitudeRandom() {
    this.amplitudeRandom = this.random(this.amplitudeRandomMin, this.amplitudeRandomMax);
  }

  getPhase() {
    if (this.phaseRandomMode) {
      if (this.nowMillis > this.lastRandomPhaseExecutionChange + this.randomPhaseExecutionPeriod) {
        this.lastRandomPhaseExecutionChange = this.nowMillis;
        this.updatePhaseRandom();
      }
      return this.phaseRandom;
    }
    return this.phase;
  }

  updatePhaseRandom() {
    this.phaseRandom = this.random(this.phaseRandomMin, this.phaseRandomMax);
  }

  getOffset() {
    if (this.offsetRandomMode) {
      if (this.nowMillis > this.lastRandomOffsetExecutionChange + this.randomOffsetExecutionPeriod) {
        this.lastRandomOffsetExecutionChange = this.nowMillis;
        this.updateOffsetRandom();
      }
      return this.offsetRandom;
    }
    return this.offset;
  }

  updateOffsetRandom() {
    this.offsetRandom = this.random(this.offsetRandomMin, this.offsetRandomMax);
  }

  random(min, max) {
    return Math.floor(Math.random() * (max - min + 1)) + min;
  }

  constrain(value, min, max) {
    //console.log('constrain: '+value+'/' + min + '/' + max);
    if (value < min) {
      return min;
    } else if (value > max) {
      return max;
    }
    return value;
  }

  map(x, in_min, in_max, out_min, out_max) {
    const run = in_max - in_min;
    if (run === 0) {
      return -1;
    }
    const rise = out_max - out_min;
    const delta = x - in_min;
    const dd = (delta * rise) / run + out_min;
    return Math.round(dd);
  }

  setEnabled(enable) {
    this.enabled = enable;
    if (this.enabled) {
      this.updatePhaseIncrement();
    }
  }

  setRange(min, max) {
    this.m_min = min;
    this.m_max = max;
  }

  setUpdate(value) {
    this.updateRate = value;
    this.updatePhaseIncrement();
  }

  setPeriod(value) {
    this.period = value;
    this.updatePhaseIncrement();
  }

  setPeriodRandom(value) {
    this.periodRandomMode = value;
    if (value) {
      this.updatePeriodRandom();
    } else {
      this.updatePhaseIncrement();
    }
  }

  setPeriodRandomMin(min) {
    this.periodRandomMin = min;
    if (this.periodRandomMode) {
      this.updatePeriodRandom();
    }
  }

  setPeriodRandomMax(max) {
    this.periodRandomMax = max;
    if (this.periodRandomMode) {
      this.updatePeriodRandom();
    }
  }

  setOffset(value) {
    this.offset = this.mapTCodeToDegrees(value);
  }

  setOffsetRandom(value) {
    this.offsetRandomMode = value;
    if (value) {
      this.updateOffsetRandom();
    }
  }

  setOffsetRandomMin(min) {
    this.offsetRandomMin = this.mapTCodeToDegrees(min);
    if (this.offsetRandomMode) {
      this.updateOffsetRandom();
    }
  }

  setOffsetRandomMax(max) {
    this.offsetRandomMax = this.mapTCodeToDegrees(max);
    if (this.offsetRandomMode) {
      this.updateOffsetRandom();
    }
  }

  setAmplitude(value) {
    this.amplitude = this.map(value, 0, 100, 0, 90);
  }

  setAmplitudeRandom(value) {
    this.amplitudeRandomMode = value;
    if (value) {
      this.updateAmplitudeRandom();
    }
  }

  setAmplitudeRandomMin(min) {
    this.amplitudeRandomMin = this.map(min, 0, 100, 0, 90);
    if (this.amplitudeRandomMode) {
      this.updateAmplitudeRandom();
    }
  }

  setAmplitudeRandomMax(max) {
    this.amplitudeRandomMax = this.map(max, 0, 100, 0, 90);
    if (this.amplitudeRandomMode) {
      this.updateAmplitudeRandom();
    }
  }

  setMotionRandomChangeMin(min) {
    this.motionRandomChangeMin = min;
    if (this.amplitudeRandomMode || this.periodRandomMode || this.offsetRandomMode) {
      this.lastExecutionPeriodChange = 0;
      this.checkUpdateRandomExecutionPeriod();
    }
  }

  setMotionRandomChangeMax(max) {
    this.motionRandomChangeMax = max;
    if (this.amplitudeRandomMode) {
      this.lastExecutionPeriodChange = 0;
      this.checkUpdateRandomExecutionPeriod();
    }
  }

  setPhase(value) {
    this.phase = this.degreesToRadian(value);
  }

  setPhaseRandom(value) {
    this.phaseRandomMode = value;
    if (value) {
      this.updatePhaseRandom();
    }
  }

  setPhaseRandomMin(min) {
    this.phaseRandomMin = min;
    if (this.offsetRandomMode) {
      this.updatePhaseRandom();
    }
  }

  setPhaseRandomMax(max) {
    this.phaseRandomMax = max;
    if (this.offsetRandomMode) {
      this.updatePhaseRandom();
    }
  }

  setReverse(value) {
    this.reversed = value;
  }

  stopAtCycle(value) {
    this.stopAt = this.currentPhase + 2 * this.M_PI * value;
  }

  mapTCodeToDegrees(tcode) {
    return this.map(tcode, 0, 9999, -90, 90);
  }

  degreesToRadian(degrees) {
    return (degrees * this.M_PI) / 180.0;
  }
}

class MotionProfile {

  /**
   * Converts JSON data to a MotionProfile object
   * @param {Object} jsonData - The JSON configuration data
   * @returns {MotionProfile} - The created MotionProfile instance
   */
  static fromJSON(jsonData) {
    const profile = new MotionProfile();

    // Set basic profile properties
    if (jsonData.name) profile.name = jsonData.name;
    if (jsonData.reversed !== undefined) profile.reversed = jsonData.reversed;

    // Convert channels
    if (jsonData.channelList && Array.isArray(jsonData.channelList)) {
      profile.channelList = jsonData.channelList.map(channelData => {
        const channel = MotionProfile.createChannel();

        // Set all channel properties from JSON
        for (const [key, value] of Object.entries(channelData)) {
          if (channel.hasOwnProperty(key)) {
            channel[key] = value;
          }
        }

        return channel;
      });
    }

    // Convert channel ranges
    if (jsonData.channelRangeList && Array.isArray(jsonData.channelRangeList)) {
      profile.channelRangeList = jsonData.channelRangeList.map(rangeData => {
        const range = MotionProfile.createChannelRange();

        // Set all range properties from JSON
        for (const [key, value] of Object.entries(rangeData)) {
          if (range.hasOwnProperty(key)) {
            range[key] = value;
          }
        }

        return range;
      });
    }

    return profile;
  }

  constructor() {
    this.name = '';
    this.channelList = [];
    this.channelRangeList = [];
    this.reversed = false;
  }

  static createChannel() {
    return {
      name: '',
      update: 100,
      period: 2000,
      amp: 60,
      offset: 5000,
      phase: 0,
      reverse: false,
      periodRan: false,
      periodMin: 500,
      periodMax: 2000,
      ampRan: false,
      ampMin: 20,
      ampMax: 60,
      offsetRan: false,
      offsetMin: 3000,
      offsetMax: 7000,
      phaseRan: false,
      phaseMin: 0,
      phaseMax: 180,
      ranMin: 3000,
      ranMax: 30000
    };
  }

  static createChannelRange() {
    return {
      name: '',
      min: 0,
      max: 0
    };
  }
}

class TCodeProfilePlayer {
  constructor() {
    this.RUN_STATE_STOP = 1;
    this.RUN_STATE_RUN = 2;
    this.runState = this.RUN_STATE_STOP;
    this.motionGeneratorList = [];
    this.animationFrameId = null;
    this.callback = null;
  }

  static getInstance() {
    if (!TCodeProfilePlayer.instance) {
      TCodeProfilePlayer.instance = new TCodeProfilePlayer();
    }
    return TCodeProfilePlayer.instance;
  }

  isRunning() {
    return this.runState === this.RUN_STATE_RUN;
  }

  getRunState() {
    return this.runState;
  }

  stop() {
    this.runState = this.RUN_STATE_STOP;
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
    console.log("==停止===");
  }

  setProfile(file) {
    if (!file || !file.channelList || file.channelList.length <= 0) {
      return;
    }

    const temp = [];
    for (const channel of file.channelList) {
      const mg = new MotionGenerator(file.channelRangeList, channel, file.reversed);
      temp.push(mg);
    }
    this.motionGeneratorList = temp;
  }

  setCommandCallback(callback) {
    this.callback = callback;
  }

  runProfile() {
    if (this.runState === this.RUN_STATE_RUN) {
      return;
    }

    this.runState = this.RUN_STATE_RUN;

    const loop = () => {
      if (this.runState === this.RUN_STATE_STOP) {
        return;
      }

      const tempList = this.motionGeneratorList;
      if (!tempList || tempList.length <= 0) {
        this.animationFrameId = requestAnimationFrame(loop);
        return;
      }

      const now = Date.now();
      let cmd = '';

      for (const generator of tempList) {
        const xx = generator.calculateNext(now);
        if (xx) {
          cmd += ' ' + xx;
        }
      }

      cmd = cmd.trim();
      if (cmd && this.callback) {
         //console.log("发送：" + cmd);
        this.callback(cmd);
      }

      this.animationFrameId = requestAnimationFrame(loop);
    };

    this.animationFrameId = requestAnimationFrame(loop);
  }
}

// // Example usage
// function profileTest() {
//     const player = TCodeProfilePlayer.getInstance();
//
//     // Set the command callback
//     player.setCommandCallback((cmd) => {
//         console.log("Executing command:", cmd);
//         // Here you would actually send the command to your device
//     });
//
//     // Create a profile
//     const profile = new MotionProfile();
//     const channels = [];
//
//     const L0 = MotionProfile.createChannel();
//     L0.name = "L0";
//     L0.amp = 80;
//
//     const R0 = MotionProfile.createChannel();
//     R0.name = "L1";
//     R0.amp = 80;
//
//     channels.push(L0);
//     profile.channels = channels;
//
//     console.log("初始状态:", player.isRunning());
//     player.stop();
//     player.runProfile();
//
//     setTimeout(() => {
//         console.log("开始运行状态:", player.isRunning());
//         player.setProfile(profile);
//     }, 1000);
//
//     setTimeout(() => {
//         // Change script
//         const newChannels = [R0, R0, R0, R0];
//         profile.channels = newChannels;
//         console.log("换脚本================");
//         player.setProfile(profile);
//     }, 3000);
//
//     setTimeout(() => {
//         // Change script again
//         const newChannels = [L0, L0];
//         profile.channels = newChannels;
//         console.log("换脚本================");
//         player.setProfile(profile);
//     }, 5000);
// }

// // Run the test
// profileTest();
//

export {MotionGenerator, MotionProfile, TCodeProfilePlayer};



// WEBPACK FOOTER //
// ./src/tcode/TCodeProfilePlayer2.js